import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
// ngrx store
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

// models
import { AppState } from '../../models/app-state.interface';
import { LoadingStatusInitState } from '../../../shared/models/loading-status.interface';
import { TimeslotBookingObject } from '../../models/timeslot-reservation.interface';
import { TimeslotEmitData } from '../../models/timeslot-emit-data.interface';
// service
import { BookingService } from '../../services/booking.service';
import { CollectionService } from '../../services/collection.service';
import { EventsService } from '../../services/events.service';
import { PriceService } from '../../../booking/services/price.service';
import { AppStateService } from '../../../shared/services/app-state.service';
import { CopyService } from '../../../shared/services/app-copy.service';
import { RulesCopyService } from '../../../shared/services/app-rules-copy.service';
import { FlightDetailsService } from '../../../shared/services/flight-details.service';
import { ErrorHandler } from '../../../shared/services/error-handler.service';
import { DataStorageService } from '../../../shared/services/data-storage.service';

import moment from 'moment';

declare var ga: any;

@Component({
  selector: 'app-step-time',
  templateUrl: './step-time.component.html',
  styleUrls: ['./step-time.component.scss']
})
export class StepTimeComponent implements OnInit, OnDestroy {

  private _bookingInitSubscription: Subscription;
  private _loadingSubscription: Subscription;
  private _priceSubscription: Subscription;
  private _bookingType: string;
  private _bookingFlightDateAmend: boolean = false;
  private _bookingJourneyReference: string;
  private _bookingCarrier: string;
  private _reservationEndTime: string;
  private _errorExpiredCount = 0;
  private _errorReserveCount = 0;
  private _firstLoad = true;
  private _isDomestic = false;
  private _destinationCountry: string = null;

  public bookingInit = false;
  public storeFlightData: any;
  public bookingProduct: string;
  public flightDirection;
  public zoneInfo;
  public ccdAvailable: any;
  public flightProducts;
  public hasManualFlightData: Boolean = false;
  public flightData;
  public manualFlightData;
  public flightFinalDestination;
  public requestDepartureDate;
  public requestArrivalDate;
  public copyLoaded: Boolean = false;
  public appCopy;
  public airlineCode;
  public originalAirlineCode;
  public location;
  public flightNumber;
  public collectionDateTime;
  public flightDateTime;
  public departureAirportCode;
  public terminal;
  public operatingCarrier;
  public isAirportToAirport;
  public showLoader;
  public hasReserveError = { hasError: false, count: 0 };
  public hasReserveExpiredError = { hasError: false, count: 0 };
  public bookingData: TimeslotEmitData = {
    booking: {
      date: '',
      productTierId: null,
      timeslotId: null,
      changedBagsFlag: false,
      changedLocationFlag: false,
      changedTimeslotFlag: false
    },
    timeslot: {
      timeslot: '',
      date: '',
      bookingReferenceId: '',
      timeSlotId: '',
      overrideTimeSlotCapacity: null,
      departureAirportCode: '',
    }
  };

  constructor(
    private _appStore: Store<AppState>,
    private _appStateService: AppStateService,
    private _bookingService: BookingService,
    private _collectionService: CollectionService,
    private _eventsService: EventsService,
    private _priceService: PriceService,
    private _copyService: CopyService,
    private _rulesCopyService : RulesCopyService,
    private _errorService: ErrorHandler,
    private _activatedRoute: ActivatedRoute,
    private _flightDetailsService: FlightDetailsService,
    private _dataStorageService: DataStorageService,
    private _router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {

    if(this._activatedRoute.snapshot.queryParams.d2d === 'falseD2D') {
      this._appStore.dispatch({ type: 'UPDATE_D2D_SERVICE', payload: false });
      this._dataStorageService.saveData()
    }
    this._bookingInitSubscription = this._appStore.select('loadedStatusReducer')
      .subscribe((res: any) => {
        // if booking data has bookingLoaded
        // show template and subscribe to data
        if (res.currentBookingLoaded && this._firstLoad) {
          this.bookingInit = res.currentBookingLoaded;
          this.setDataSubscriptions();
          this._firstLoad = false;
        } else {
          return;
        }
      });

  }

  setDataSubscriptions() {
    this.setCurrentStoreVars();

    this._loadingSubscription = this._appStore.select('loadedStatusReducer')
      .subscribe((res: LoadingStatusInitState) => {
        this.copyLoaded = res.copyLoaded;
        if (res.copyLoaded && res.rulesCopyLoaded) {
          if (isPlatformBrowser(this.platformId)) {
            this.getCopy();
          }
        }
      });


    if (isPlatformBrowser(this.platformId)) {
      if (this._bookingType !== 'amend') {
        ga('set', 'page', '/' + this.bookingProduct + '/collection-time/' + this.airlineCode);
        ga('send', 'pageview');
      } else {
        ga('set', 'page', '/a/' + this.bookingProduct + '/collection-time');
        ga('send', 'pageview');
      }
    }
  }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }

  // set booking state to vars
  setCurrentStoreVars() {
    const currentAppState = this.getState(this._appStore);
    // booking store vars
    const bookingState = currentAppState.bookingReducer;
    this.bookingProduct = bookingState.bookingDetails.bookingProduct;
    this.airlineCode = bookingState.bookingDetails.partnerAirlineCode;
    this.originalAirlineCode = bookingState.bookingDetails.carrier.originalAirlineCode;

    // flight data
    this.storeFlightData = currentAppState.flightReducer;
    this.flightNumber = this.storeFlightData.flightDetails.flightResponse.flightStatusDetails[0].flightNumber;
    const flightData = this.storeFlightData.flightDetails.flightResponse;


    // current booking store vars
    const currentBooking = currentAppState.currentBookingReducer.currentBooking;
    // booking vars
    this.flightDirection = bookingState.bookingDetails.journeyLegDetails[0].journeyDirection;
    this.zoneInfo = bookingState.bookingDetails.zoneInfo;
    this.ccdAvailable = currentAppState.bookingReducer.bookingDetails.bookingProduct === 'ccd';
    this._bookingType = bookingState.bookingDetails.bookingType;
    this._bookingFlightDateAmend = bookingState.bookingDetails.bookingFlightDateAmend;
    this._bookingJourneyReference = bookingState.bookingDetails.bookingJourneyReference;
    this._bookingCarrier = bookingState.bookingDetails.carrier.airlineCode;
    this._isDomestic = currentAppState.flightReducer.flightDetails.flightProducts.isDomesticABC;
    this.isAirportToAirport = bookingState.bookingDetails.isAirportToAirport;
    this._destinationCountry = this.storeFlightData.flightDetails.flightFinalDestination.airportCountryName;

    // vars depending on direction
    if (this.flightDirection === 1) {
      this.location = currentBooking.bookingJourney[0].collectionLocation;
      this.flightDateTime = flightData.flightStatusDetails[0].departureDateTimeDetails.scheduledDateTimeLocal;
      this.departureAirportCode = flightData.flightStatusDetails[0].departureAirport.airportCode;
      this.terminal = flightData.flightStatusDetails[0].departureAirport.airportTerminal;
      this.operatingCarrier = flightData.flightStatusDetails[0].operatingAirlineCode;
    } else if(this.flightDirection === 2) {
      this.location = currentBooking.bookingJourney[0].deliveryLocation;
      this.departureAirportCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCode;
      this.flightDateTime = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalDateTimeDetails.scheduledDateTimeLocal;
      this.terminal = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportTerminal;
      this.operatingCarrier = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].operatingAirlineCode;
    } else {
      this.location = currentBooking.bookingJourney[0].deliveryLocation;
      this.flightDateTime = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalDateTimeDetails.scheduledDateTimeLocal;
      this.terminal = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportTerminal;
      this.operatingCarrier = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].operatingAirlineCode;
    }

    // this._priceSubscription = this._priceService.priceUpdating$
    //   .subscribe(isUpdating => {
    //     this.showLoader = isUpdating
    //   });

      if(!bookingState.bookingDetails.isD2dService) {
        this._appStore.dispatch({ type: 'UPDATE_AUTO_REFRESH_TIME_SERVICE', payload: moment() });
      }
  }

  getCopy() {
    const panelHeaderCopy = this._copyService.getCopyBySection(
      this.airlineCode,
      window.location.pathname.split('/')[2],
      this.flightDirection === 1 ? ['panelHeader',  'outbound', 'CCDArrivalsUpsell'] : ['panelHeader', 'inbound', 'ABCArrivalsUpsell']
    );

    const zeroTimeSlot = this._copyService.getCopyBySection(
      this.airlineCode,
      window.location.pathname.split('/')[2],
      this.flightDirection === 1 ? ['abc_outbound', 'zeroTimeslots', 'emailScreen'] : ['ccd_inbound', 'zeroTimeslots', 'emailScreen']
    );

    const copyParam = this._isDomestic === true ? 'identityRules' : 'passengerRules';
    const isUSA = this._destinationCountry.toLocaleUpperCase() === 'US';

    const passengerRulesCopy = this._rulesCopyService.getRuleByAirlineKey(this.airlineCode, copyParam, isUSA)

    this.appCopy = {
      panelHeader: panelHeaderCopy.reduce((acc: any, current, ind) => {
        acc = {...acc, ...current};
        return acc;
      }, {}),
      passengerRules: passengerRulesCopy,
      zeroTimeSlot: zeroTimeSlot[0]
    };

    console.log(this.appCopy)

  }

  timeslotDataStillValid(hasReservedTime, data) {

    let amendChangedFlag = true;

    // hack to fix new booking and amends
    // as we don't save the changed flags
    // we check, on amends, for the amend flag value
    // on new bookings we just test the expiry time on the timeslot reservation
    if (this._bookingType === 'amend' && data.booking.changedTimeslotFlag) {
      amendChangedFlag = false;
    } else if (this._bookingType !== 'amend' && !data.booking.changedTimeslotFlag) {
      amendChangedFlag = false;
    }

    if (hasReservedTime && !amendChangedFlag) {
      // if reservation has expired
      // try to reserve timeslot again
      if (!this.checkSelectedTime()) {
        this.hasReserveExpiredError = { count: this._errorExpiredCount++, hasError: true };
        const timeslotBookingObj: TimeslotBookingObject = {
          timeSlotReservationId: '',
          reservationStartTime: '',
          reservationEndTime: '',
          timeSlotType: null
        };

        this.timeslotReservationToBooking(timeslotBookingObj);
        // this.reserveTimeslot(data);
        return;
      } else {

        if (this._bookingType === 'amend') {

          // if timeslot reservation still ok
          // continue to payment
          let amendFlag;
          if (this._bookingFlightDateAmend && data.booking.changedBagsFlag && data.booking.changedLocationFlag && data.booking.changedTimeslotFlag) {
            amendFlag = 57;
          } else if(data.booking.changedBagsFlag && data.booking.changedLocationFlag && data.booking.changedTimeslotFlag) {
            amendFlag = 56
          }else if (data.booking.changedLocationFlag && data.booking.changedTimeslotFlag) {
            amendFlag = 48;
          } else if (data.booking.changedBagsFlag && data.booking.changedLocationFlag) {
            amendFlag = 40;
          } else if (data.booking.changedBagsFlag && data.booking.changedTimeslotFlag) {
            amendFlag = 24;
          } else if (data.booking.changedTimeslotFlag) {
            amendFlag = 16;
          } else if (data.booking.changedBagsFlag) {
            amendFlag = 8;
          }

          const amendData = {
            reference: this._bookingJourneyReference,
            flag: amendFlag
          };
          const priceHasDifference = this.getState(this._appStore).priceReducer.price.priceHistory.majorAmount === 0 ? false : true;
          if (priceHasDifference) {
            this._bookingService.postAmend(amendData, true);
            this.goPanelForward();
          } else {
            this._bookingService.postAmend(amendData, false);
          }
          return;

        }
      }
    }

    this.reserveTimeslot(data);
  }


  checkReserveTimeslotData(data: TimeslotEmitData) {
    // check if timeslot is already reserved
    // if so move forward
    // else reserve again

    if (this._bookingType !== 'amend') {

      this._reservationEndTime = this.getState(this._appStore).currentBookingReducer.currentBooking.timeSlotReservation.reservationEndTime;
      const hasReservedTime = this._reservationEndTime !== '' && typeof this._reservationEndTime !== 'undefined';
      this.timeslotDataStillValid(hasReservedTime, data);

    } else {
      // if timeslot data is empty
      // set timeslot changed flag to false
      if (data.timeslot.timeSlotId === '' || data.timeslot.date === '') {
        data.booking.changedTimeslotFlag = false;
      } else {
        data.booking.changedTimeslotFlag = true;
      }

      // if amend obj is empty then this is the first time on the panel for amend
      // so read bagChangedFlag
      // else compare amendBooking obj bag value with original value
      let amendObj = null;
      if (Object.keys(this.getState(this._appStore).currentBookingReducer.amendBooking).length !== 0) {
        amendObj = this.getState(this._appStore).currentBookingReducer.amendBooking;
        console.log(amendObj.amendBookingFlag)
        // set flags based on store amend value
        // this fixes issue when going between panels on amends
        if (amendObj.amendBookingFlag === 8) {
          data.booking.changedBagsFlag = true;
        }
        if (amendObj.amendBookingFlag === 16) {
          data.booking.changedTimeslotFlag = true;
        }
        if (amendObj.amendBookingFlag === 24 || (data.booking.changedTimeslotFlag && amendObj.amendBookingFlag === 8) || (data.booking.changedBagsFlag && amendObj.amendBookingFlag === 16)) {
          data.booking.changedTimeslotFlag = true;
          data.booking.changedBagsFlag = true;
        }
        if (amendObj.amendBookingFlag === 32) {
          data.booking.changedLocationFlag = true;
        }
      }

      // if timeslot hasn't changed but price is different - bags changed
      // go to payment
      // else if bags have changed but price hasn't changed
      // post amend
      if (!data.booking.changedTimeslotFlag && this.getState(this._appStore).priceReducer.price.priceHistory.majorPriceDifference !== 0) {
        this.sendToBooking(data);
        return;
      } else if (!data.booking.changedTimeslotFlag && this.getState(this._appStore).priceReducer.price.priceHistory.majorPriceDifference === 0) {
        this.sendToBooking(data);
        return;
      }

      if (data.booking.changedTimeslotFlag) {

        if (!data.booking.changedLocationFlag) {
          if (typeof this.getState(this._appStore).currentBookingReducer.amendBooking.addBookingRequest === 'undefined') {
            this._reservationEndTime = '';
          } else {
            this._reservationEndTime = this.getState(this._appStore).currentBookingReducer.amendBooking.addBookingRequest.timeSlotReservation.reservationEndTime;
          }
          const hasReservedTime = this._reservationEndTime !== '' && typeof this._reservationEndTime !== 'undefined';
          this.timeslotDataStillValid(hasReservedTime, data);
        } else {
          this.timeslotDataStillValid(false, data);
        }

      }

    }

  }

  reserveTimeslot(data) {

    this._collectionService.reserveTimeslotPromise(data.timeslot).then((res: any) => {

      if (res.error) {
        // send to child UI
        this.hasReserveError = { hasError: true, count: this._errorReserveCount++ };
        return;
      }

      const timeslotBookingObj: TimeslotBookingObject = {

        timeSlotReservationId: res.reservationId,
        reservationStartTime: res.reservationStartTime,
        reservationEndTime: res.reservationEndTime,
        timeSlotType: res.slotType
      };
      this._appStore.dispatch({ type: 'UPDATE_RESERVED_TIMESLOT', payload: data.timeslot.timeSlotId });
      this._appStore.dispatch({type: 'UPDATE_IS_BOOKING_COMPLETE', payload: { isCompleted: true, _bookingType: this.bookingProduct === 'abc' ? 'outboundBooking' : 'inboundBooking' }})
      this._appStore.dispatch({ type: 'UPDATE_CUT_OFF_WINDOW', payload: res.cutOffTimeWindow });
      this._appStore.dispatch({ type: 'UPDATE_AMEND_CUT_OFF_WINDOW', payload: res.amendCutOffTimeWindow });
      this.timeslotReservationToBooking(timeslotBookingObj);
      this.sendToBooking(data);

    })
      .catch((err: any) => { return; });

  }

  checkSelectedTime() {

    let result = true;
    const endTime = moment(this._reservationEndTime);
    const now = moment.utc();

    if (endTime.isBefore(now) && !endTime.isSame(now)) {
      result = false;
    }

    return result;

  }

  updateAirportTimeInBooking(e) {
    this._bookingService.updateAirportTime(e.direction, e.date);
  }

  timeslotReservationToBooking(data) {
    this._bookingService.updateTimeslotReservation(data);
  }

  updatePrice(bagCount) { // add new bagItems?
    this._appStateService.setVirtualBags(bagCount);
    this._bookingService.updateVirtualBags(bagCount);
    this._priceService.updatePriceBags(bagCount);
  }

  sendBagNote(data) {
    this._bookingService.updateBagNote(data);
  }

  sendBagItems(data) {
    this._appStateService.setBagItems(data);
    this._bookingService.updateBagItems(data);
  }
  sendVirtualBags(data) {
    this._appStateService.setVirtualBags(data);
    this._bookingService.updateVirtualBags(data);
    this._priceService.updatePriceBags(data);
  }

  sendHasOversizedBag(data) {
    this._bookingService.updateHasOversizedBag(data);
  }

  sendToBooking(data: TimeslotEmitData) {
    let amendFlag;

    if (this._bookingFlightDateAmend && data.booking.changedBagsFlag && data.booking.changedLocationFlag && data.booking.changedTimeslotFlag) {
      amendFlag = 57;
    } else if(data.booking.changedLocationFlag && data.booking.changedTimeslotFlag){
      amendFlag = 56;
    } else if (data.booking.changedLocationFlag && data.booking.changedTimeslotFlag) {
      amendFlag = 48;
    } else if (data.booking.changedBagsFlag && data.booking.changedLocationFlag) {
      amendFlag = 40;
    } else if (data.booking.changedBagsFlag && data.booking.changedTimeslotFlag) {
      amendFlag = 24;
    } else if (data.booking.changedTimeslotFlag) {
      amendFlag = 16;
    } else if (data.booking.changedBagsFlag) {
      amendFlag = 8;
    }

    const timeSlotDateTimeFlagNotSaved = this.getState(this._appStore).currentBookingReducer.currentBooking.bookingCompletion.collectionDateTimeDetails && this.getState(this._appStore).currentBookingReducer.currentBooking.bookingCompletion.collectionDateTimeDetails;
    if (data.booking.changedTimeslotFlag || !timeSlotDateTimeFlagNotSaved) {

      if (this.bookingProduct === 'abc') {
        this._bookingService.updateCollectionOrDeliveryDateTime(this.flightDirection, data.booking.date);
        this._bookingService.updateTimeslotProductTierId(data.booking.productTierId);
        this._bookingService.updateTimeslotId(data.booking.timeslotId);
        this._bookingService.updateBookingFlagsCollectionDateTime(true);
        this._bookingService.updateBookingFlagsDeliveryDateTime(true);
      } else if (this.bookingProduct === 'ccd') {

        if (this.flightDirection === 1) {
          this._bookingService.updateCollectionOrDeliveryDateTime(this.flightDirection, data.booking.date);
          this._bookingService.updateCollectionOrDeliveryDateTime(2, data.booking.dateAirport);
          this._bookingService.updateTimeslotProductTierId(data.booking.productTierId);
          this._bookingService.updateTimeslotId(data.booking.timeslotId);
          this._bookingService.updateBookingFlagsCollectionDateTime(true);
          this._bookingService.updateBookingFlagsDeliveryDateTime(true);
        } else {
          this._bookingService.updateCollectionOrDeliveryDateTime(2, data.booking.date);
          this._bookingService.updateCollectionOrDeliveryDateTime(1, data.booking.dateAirport);
          this._bookingService.updateTimeslotProductTierId(data.booking.productTierId);
          this._bookingService.updateTimeslotId(data.booking.timeslotId);
          this._bookingService.updateBookingFlagsDeliveryDateTime(true);
        }
      }
    }

    if (this._bookingType !== 'amend') {
      const getState = this.getState(this._appStore);
      const bookingState = JSON.parse(JSON.stringify(getState));
      const d2dBookingReducer = bookingState.d2dReducer;
      const flightReducer = bookingState.flightReducer;
      const bookingReducer = bookingState.bookingReducer;
      const currentBookingReducer = bookingState.currentBookingReducer;
      const priceReducer = bookingState.priceReducer;
      const timeslotReducer = bookingState.timeslotReducer;
      const flightFinderReducer = bookingState.flightFinderReducer;
      const hubDropReducer = bookingState.hubDropReducer;
      const bookingDetails = bookingState.bookingReducer.bookingDetails;
      const isD2dService = bookingDetails.isD2dService;
      const currentBookingType = bookingDetails.bookingProduct;
      const isABC = bookingState.d2dReducer.flightDetails.flightProducts.abc;
      const isCCD = bookingState.d2dReducer.flightDetails.flightProducts.ccd;
      // const isUpsellBookingComplete = bookingDetails.bookingProduct === 'abc' ? getState.d2dReducer.inboundBooking?.isCompleted : getState.d2dReducer.inboundBooking?.isCompleted;
      // if isD2d booking and bookingcount is gt 1 go farword otherwise go for 2nd booking

      if (((!isD2dService && isABC && isCCD) || (!isD2dService && getState.d2dReducer.hasSession && isABC && isCCD)) && getState.d2dReducer.canAvailD2d) {
        // redirect to upsell
        const url = bookingDetails.bookingProduct === 'abc' ? 'upgrade-ccd' : 'upgrade-abc';
        // this._router.navigate([url])
        this._eventsService.goToPanelByPath(url);
      }
      // else if (isD2dService && !isUpsellBookingComplete) {
      //   const state = this.getState(this._appStore);
      //   const isD2dService = state.bookingReducer.bookingDetails.isD2dService;
      //   const currentBookingType = state.bookingReducer.bookingDetails.bookingProduct;
      //   if (isD2dService) {
      //     const url = currentBookingType === "abc" ? "delivery-time" : "collection-time"
      //     const upsellBookingType = currentBookingType !== "abc" ? "abc" : "ccd"
      //     this._eventsService.goToBookingSwitcher(upsellBookingType, url, null, null, true);
      //   }
      // }
      else {
        this._appStore.dispatch({ type: 'SAVE_BOOKING_DETAILS', payload: { bookingType: currentBookingType === 'abc' ? 'outboundBooking' : 'inboundBooking', flightReducer, bookingReducer, currentBookingReducer, priceReducer, timeslotReducer, flightFinderReducer, hubDropReducer, isCompleted: true } });
        this.goPanelForward();
      }
    } else {

      const amendData = {
        reference: this._bookingJourneyReference,
        flag: amendFlag
      };

      if (data.booking.changedTimeslotFlag) {
        this._bookingService.updateMakePaymentProductTierId(data.booking.productTierId);
        this._bookingService.updateMakePaymentTimeslotId(data.booking.timeslotId);
      }

      // if price has changed (but booking wasn't paid by invoice nor booked through a partner website)
      // save amend data and redirect to payment screen to take payment
      // otherwise send amend data to BE and redirect to booking details
      const priceHasDifference = this.getState(this._appStore).priceReducer.price.priceHistory.majorAmount === 0 ? false : true;
      const isPaidByInvoice = this.getState(this._appStore).bookingReducer.bookingDetails.bookingAmendPaidByInvoice;
      const isBookedWithPartner = this.getState(this._appStore).bookingReducer.bookingDetails.bookingSource !== null;
      if (priceHasDifference && !isPaidByInvoice && !isBookedWithPartner) {
        this._bookingService.postAmend(amendData, true);
        this.goPanelForward();
      } else {
        this._bookingService.postAmend(amendData, false);
      }

    }
  }

  handleShowModal(modalData) {
    this._errorService.handleModal(modalData);
  }

  goPanelBack() {
    this._eventsService.goToPanel('prev');
  }
  goPanelForward() {
    this._eventsService.goToPanel('next');
  };


  ngOnDestroy() {
    this._bookingInitSubscription.unsubscribe();
    if (this._loadingSubscription) { this._loadingSubscription.unsubscribe(); }
    if (this._priceSubscription) { this._priceSubscription.unsubscribe(); }
  }

}
